import { type LineChartDataPoint, type LineChartCategory } from './types';

const DEFAULT_Y_DOMAIN = [0, 20];
const DEFAULT_Y_TICKS = [0, 4, 8, 12, 16, 20];

const calculateNiceStep = (range: number, targetSteps: number): number => {
  const roughStep = range / targetSteps;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalizedStep = roughStep / magnitude;

  let niceStep = 10;
  if (normalizedStep <= 1) niceStep = 1;
  if (normalizedStep > 1 && normalizedStep <= 2) niceStep = 2;
  if (normalizedStep > 2 && normalizedStep <= 5) niceStep = 5;

  return niceStep * magnitude;
};

export const calculateDefaultYDomain = <T extends LineChartDataPoint>(
  data: T[],
  categories: LineChartCategory[]
): {
  yDomain: [number, number];
  yTicks: number[];
} => {
  if (!data.length || !categories.length) {
    return { yDomain: [...DEFAULT_Y_DOMAIN] as [number, number], yTicks: [...DEFAULT_Y_TICKS] };
  }

  const allValues = data.flatMap(point =>
    categories
      .map(cat => point[cat.key])
      .filter((value): value is number => typeof value === 'number')
  );

  if (!allValues.length) {
    return { yDomain: [...DEFAULT_Y_DOMAIN] as [number, number], yTicks: [...DEFAULT_Y_TICKS] };
  }

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const margin = Math.max(1, (maxValue - minValue) * 0.1);
  const domain: [number, number] = [
    Math.max(0, Math.floor(minValue - margin)),
    Math.ceil(maxValue + margin),
  ];

  const range = domain[1] - domain[0];
  const niceStep = calculateNiceStep(range, 5);
  const niceMin = Math.floor(domain[0] / niceStep) * niceStep;
  const niceMax = Math.ceil(domain[1] / niceStep) * niceStep;
  const numTicks = Math.round((niceMax - niceMin) / niceStep) + 1;

  const ticks = Array.from(
    { length: numTicks },
    (_, i) => Math.round((niceMin + niceStep * i) * 100) / 100
  );

  return { yDomain: [niceMin, niceMax], yTicks: ticks };
};

export const createChartCategories = <T extends string>(
  keys: readonly T[],
  getLabel: (key: string) => string,
  getColor: (key: T) => string
): LineChartCategory[] =>
  keys.map(key => ({
    key,
    label: getLabel(key),
    color: getColor(key),
  }));
